/**
 * Entry point for @arbocollab/arbo-ckeditor-custom-build
 * - Provides named ESM exports (tree-shakable)
 * - Provides a global singleton for CDN/UMD usage: window.ArboCKEditor
 * - Prevents multiple initializations when loaded more than once
 */

const GLOBAL_KEY = "__ARBO_CKEDITOR_SINGLETON__";

// Import a stable symbol from CKEditor 5 core to detect distinct builds.
import { Editor as CoreEditorBase } from "@ckeditor/ckeditor5-core";

/**
 * Duplicate CKEditor 5 protection.
 * If another (different) CKEditor 5 build (separate module graph) is already on the page,
 * abort this one early to avoid "ckeditor-duplicated-modules" runtime crashes.
 */
(function guardDuplicateCKEditor() {
  try {
    if (typeof globalThis === "undefined") return;
    const g: any = globalThis;
    const KEY = "__ARBO_CKEDITOR5_ENGINE_SET__";
    const engineRef = CoreEditorBase;

    if (!g[KEY]) {
      // First build encountered.
      g[KEY] = new Set([engineRef]);
      return;
    }

    const set: Set<any> = g[KEY];
    // Same instance (deduped by bundler) -> OK.
    if (set.has(engineRef)) return;

    // Different engine object -> second independent copy.
    console.error(
      "[arbo-ckeditor-custom-build] Detected a second distinct CKEditor 5 build on the page. " +
        "Aborting this build to prevent duplicated-modules errors. Remove the extra script/import."
    );
    console.trace("[arbo-ckeditor-custom-build] Trace:");

    // Mark aborted to help diagnostics.
    g.__ARBO_CKEDITOR_DUP_ABORTED__ = true;

    // Throw to stop further module evaluation (prevents partial init).
    throw new Error("CKEditor 5 duplicate build detected (arbo).");
  } catch (e) {
    // Re-throw to ensure hard failure (intentional).
    throw e;
  }
})();

export * from "./ckeditor-plugins";

export {
  default as ClassicEditor,
  createClassicEditor,
} from "./classic-editor";

export {
  default as BalloonEditor,
  createBalloonEditor,
} from "./balloon-editor";

// Styles side effect
import "./styles.ts";

// Build a minimal public API object for global exposure (do not eagerly pull plugin exports)
const buildApi = {
  ClassicEditor: undefined as any as typeof import("./classic-editor").default, // will be reassigned below
  createClassicEditor:
    undefined as any as typeof import("./classic-editor").createClassicEditor,
  BalloonEditor: undefined as any as typeof import("./balloon-editor").default,
  createBalloonEditor:
    undefined as any as typeof import("./balloon-editor").createBalloonEditor,
};

// Re-import concrete values AFTER exports to avoid circular type issues
import ClassicEditorImpl, {
  createClassicEditor as createClassicEditorImpl,
} from "./classic-editor";
import BalloonEditorImpl, {
  createBalloonEditor as createBalloonEditorImpl,
} from "./balloon-editor";

buildApi.ClassicEditor = ClassicEditorImpl;
buildApi.createClassicEditor = createClassicEditorImpl;
buildApi.BalloonEditor = BalloonEditorImpl;
buildApi.createBalloonEditor = createBalloonEditorImpl;

// Attach / reuse singleton in global scope (browser or other JS hosts)
(function attachGlobal() {
  if (typeof globalThis === "undefined") return;

  const g: any = globalThis;

  if (g[GLOBAL_KEY]) {
    return;
  }

  g[GLOBAL_KEY] = buildApi;

  if (typeof window !== "undefined") {
    if (!Object.prototype.hasOwnProperty.call(window, "ArboCKEditor")) {
      (window as any).ArboCKEditor = buildApi;
    }
  }
})();

// Optional: export the singleton reference (helps consumers who want to be explicit)
export const ArboCKEditorGlobal =
  (typeof globalThis !== "undefined" && (globalThis as any)[GLOBAL_KEY]) ||
  buildApi;
